import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import CustomModal from "@/components/modal/CustomModal";
import type { TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import { Pen, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import { BtnSimpleIcon } from "@/components/ui/button/btn-simple-icon";
import { sortTeamsByName } from "@/utils/team-sort-util";

type TeamManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 모달 하단 저장 — 초안을 본 상태에 반영 */
  onSave: () => void;
  teams: TeamsSchema;
  memberCountByTeam: Record<number, number>;
  onAddTeam: (name: string) => number | undefined;
  onRenameTeam: (id: number, name: string) => void;
  onDeleteTeam: (id: number) => void;
};

export default function TeamManagementModal({
  isOpen,
  onClose,
  onSave,
  teams,
  memberCountByTeam,
  onAddTeam,
  onRenameTeam,
  onDeleteTeam,
}: TeamManagementModalProps) {
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [flashTeamId, setFlashTeamId] = useState<number | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * 임시 팀(음수 id): id가 작을수록 최신 → 오름차순으로 최신이 위
   * 서버 팀(양수 id): id가 클수록 최신 → 내림차순
   * 임시 행은 서버 팀보다 위에 둠
   */
  const sortedTeams = useMemo(
    () => sortTeamsByName(teams),
    [teams]
  );

  useEffect(() => {
    if (!isOpen) {
      setNewTeamName("");
      setEditingTeamId(null);
      setEditingName("");
      setFlashTeamId(null);
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (editingTeamId === null) return;
    if (!sortedTeams.some((t) => t.team_id === editingTeamId)) {
      setEditingTeamId(null);
      setEditingName("");
    }
  }, [editingTeamId, sortedTeams]);

  if (!isOpen) return null;

  return (
    <CustomModal.Panel
      onClose={onClose}
      className="w-[560px] max-w-[92vw] px-6"
      button
      btnCancelText="닫기"
      btnSuccessText="저장"
      btnCancelOnClick={onClose}
      btnSuccessOnClick={onSave}
    >
      <h2 className="pt-1 font-pretendard text-2xl mb-4 text-sig text-center font-semibold">
        Team 관리
      </h2>

      <div className="mb-4 flex items-center gap-2">
        <Input
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          placeholder="추가할 팀 이름을 입력해주세요."
          className="h-10 flex-1 rounded-md  px-3 text-sm outline-none "
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const trimmed = newTeamName.trim();
            if (!trimmed) return;
            const newId = onAddTeam(trimmed);
            setNewTeamName("");
            if (typeof newId === "number") {
              if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
              }
              setFlashTeamId(newId);
              /** 짧은 피드백만: 제거 후 ring·shadow는 duration-200으로 빠르게 정리 */
              flashTimeoutRef.current = setTimeout(() => {
                setFlashTeamId(null);
                flashTimeoutRef.current = null;
              }, 1000);
            }
          }}
        >
          + 추가
        </Button>
      </div>

      <div className="max-h-[390px] pb-4 space-y-2 overflow-auto scrollbar-float ">
        {sortedTeams.length === 0 && (
          <div className="text-center text-sm py-3 text-muted-foreground">
            생성된 팀이 없습니다.
          </div>
        )}
        {sortedTeams.map((team) => {
          const isEditing = editingTeamId === team.team_id;
          const memberCount = memberCountByTeam[team.team_id] ?? 0;
          return (
            <div
              key={team.team_id}
              className={cn(
                "flex min-w-0 items-center gap-2 rounded-md border border-border transition-all! bg-transparent px-3 py-2  duration-200 ease-out group",
                flashTeamId === team.team_id && "border-sig/30 bg-sig/5"
              )}
            >
              <div className="min-w-0 flex-1 flex flex-col">
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                  <div className="min-w-0 overflow-hidden">
                    {isEditing ? (
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                        className="w-full min-w-0 border border-input bg-muted/30 rounded-sm p-2 px-2 text-sm font-semibold text-foreground outline-none ring-0 transition-colors placeholder:text-muted-foreground focus-visible:border-accent-foreground/30"
                      />
                    ) : (
                      <p
                        className="min-w-0 truncate py-1 pl-0 pr-2 text-left text-sm font-semibold"
                        title={team.team_name}
                      >
                        {team.team_name}
                      </p>
                    )}
                  </div>

                  <div className="shrink-0">
                    {isEditing ? (
                      <Button
                        size="sm"
                        type="button"
                        variant={"ghost"}
                        onClick={() => {
                          onRenameTeam(team.team_id, editingName);
                          setEditingTeamId(null);
                          setEditingName("");
                        }}
                      >
                        확인
                      </Button>
                    ) : (
                      <button
                        type="button"
                        className="p-1.5 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setEditingTeamId(team.team_id);
                          setEditingName(team.team_name);
                        }}
                        aria-label={`${team.team_name} 이름 수정`}
                      >
                        <Pen className="size-4" />
                      </button>
                    )}
                    <BtnSimpleIcon
                      tone="destructive"
                      onClick={() => onDeleteTeam(team.team_id)}
                      aria-label={`${team.team_name} 삭제`}
                      icon={<Trash2 className="size-4" />}
                    />
                  </div>
                </div>
                <p className="text-xs mt-1 text-muted-foreground flex items-center gap-1">
                  {memberCount > 0 ? `${memberCount}명` : "인원 없음"}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </CustomModal.Panel>
  );
}
